/**
 * Global promise-based confirm — thay Dialog JSX riêng cho unsaved / hỏi yes-no.
 * Delete + API: dùng ConfirmDialog local + busy, không dùng hook này.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import ConfirmDialog from '@/shared/ui/feedback/ConfirmDialog';

export interface ConfirmOptions {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;
type Resolver = (value: boolean) => void;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({ message: '' });
  const resolverRef = useRef<Resolver | null>(null);
  /** Ignore a confirm opened by the same click falling through to the dialog underneath. */
  const ignoreFallThroughRef = useRef(false);

  const settle = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOpen(false);
    if (!value) return;
    ignoreFallThroughRef.current = true;
    setTimeout(() => {
      ignoreFallThroughRef.current = false;
    }, 50);
  }, []);

  const confirm = useCallback<ConfirmFn>((next) => {
    if (ignoreFallThroughRef.current) {
      return Promise.resolve(false);
    }
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }

    setOptions(next);
    setOpen(true);

    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const contextValue = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}

      <ConfirmDialog
        open={open}
        title={options.title}
        message={options.message}
        confirmLabel={options.confirmLabel}
        cancelLabel={options.cancelLabel}
        destructive={options.destructive}
        busy={false}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmContext.Provider>
  );
};

export function useConfirm(): ConfirmFn {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return context;
}
